<template>
  <div id="page-comments-list">
    <vx-card
      ref="filterCard"
      :title="$t('comments.filters')"
      class="comment-list-filters mb-8"
      @refresh="resetColFilters"
      refreshContentAction
      collapseAction
    >
      <div class="vx-row">
        <div class="vx-col md:w-2/5 w-full">
          <label class="text-sm opacity-75">{{ $t('comments.type') }}</label>
          <v-select
            :options="typeOptions"
            :clearable="false"
            taggable
            multiple
            :placeholder="$t('forms.select_option')"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
            v-model="typeFilter"
            class="mb-4 md:mb-0"
          />
        </div>
      </div>
    </vx-card>

    <vs-table
      ref="table"
      :data="commentsData"
      :total="limit"
      :sst="true"
      @sort="handleSort"
      class="card-table vs-con-loading__container"
      id="download-loading"
    >
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
        <div>
          <!-- ITEMS PER PAGE -->
          <vs-dropdown vs-trigger-click class="cursor-pointer" v-if="commentsDataCount">
            <div
              class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
            >
              <span class="mr-2">
                {{ (currentPage * limit - (limit - 1)) | persian_digits }} {{ $t('forms.to') }}
                {{
                  commentsDataCount - currentPage * limit > 0
                    ? currentPage * limit
                    : commentsDataCount | persian_digits
                }}
                {{ $t('forms.from') }} {{ commentsDataCount | persian_digits }}
              </span>
              <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
            </div>
            <vs-dropdown-menu>
              <vs-dropdown-item @click="paginationSetPageSize(5)">
                <span>{{ 5 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(10)">
                <span>{{ 10 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(50)">
                <span>{{ 50 | persian_digits }}</span>
              </vs-dropdown-item>
              <vs-dropdown-item @click="paginationSetPageSize(100)">
                <span>{{ 100 | persian_digits }}</span>
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </div>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>

      <template slot="thead">
        <vs-th sort-key="userId">{{ $t('users.username') }}</vs-th>
        <vs-th sort-key="type">{{ $t('comments.type') }}</vs-th>
        <vs-th sort-key="route">{{ $t('comments.route') }}</vs-th>
        <vs-th sort-key="created_at">{{ $t('entity.created_at') }}</vs-th>
      </template>

      <template slot-scope="{ data }">
        <tbody id="table-body" v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <cell-user-link route="system-user-view" :user="tr.user" show-name />
            </vs-td>
            <vs-td> <cell-badges :data="tr.type" local="comments.types." /> </vs-td>
            <vs-td>
              <div class="dir-ltr" dir="ltr">{{ tr.route }}</div>
            </vs-td>
            <vs-td> {{ tr.created_at | datetime }}</vs-td>

            <template slot="expand">
              <div class="w-full">
                <div class="vx-row w-full">
                  <div class="vx-col">
                    <div class="font-semibold mt-2">{{ $t('comments.description') }}:</div>
                    <div>{{ tr.description }}</div>
                  </div>
                </div>
                <div class="vx-row w-full">
                  <div class="vx-col">
                    <div class="font-semibold mt-2">{{ $t('comments.data') }}:</div>
                    <div class="dir-ltr" dir="ltr">{{ tr.data }}</div>
                  </div>
                </div>
                <download-files class="mb-base" :files="tr.attachments" />
              </div>
            </template>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>
    <!-- PAGINATION -->
    <div>
      <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
    </div>
  </div>
</template>

<script>
import moduleUserManagement from '@/store/user-management/moduleUserManagement'
import moduleComment from '@/store/comment/moduleComment'
import CellBadges from './CellBadges'
import CellUserLink from '@/views/components/common/table-cells/CellUserLink'
import DownloadFiles from '@/views/components/forms/DownloadFiles'

export default {
  name: 'CommentList',
  components: { DownloadFiles, CellUserLink, CellBadges },
  data() {
    return {
      commentsData: [],
      // Filter Options
      typeFilter: [],

      query: {},
      sort: {},
      limit: 10,
      skip: 0
    }
  },
  watch: {
    typeFilter(obj) {
      this.setColumnFilter('type', obj)
    },
    currentPage() {
      this.updateTable()
    }
  },
  computed: {
    typeOptions() {
      return this.$store.getters['enum/selectOptions']('CommentType', 'comments.types')
    },
    commentsDataCount() {
      return this.$store.state.comment.commentsCount
    },
    totalPages() {
      return Math.ceil(this.commentsDataCount / this.limit)
    },
    currentPage: {
      get() {
        return Math.ceil(this.skip / this.limit + 1)
      },
      set(val) {
        this.skip = (val - 1) * this.limit
        this.updateTable()
      }
    }
  },
  methods: {
    updateTable() {
      this.$vs.loading({
        scale: 0.6
      })
      this.$store
        .dispatch('comment/fetchComments', {
          query: this.query,
          skip: this.skip,
          limit: this.limit
        })
        .then(res => {
          const data = JSON.parse(JSON.stringify(res.data))
          if (data.length === 0) {
            this.$vs.loading.close()
            this.commentsData = []
          }
          let count = 0
          data.forEach(comment => {
            this.$store.dispatch('userManagement/fetchUser', comment.userId).then(res => {
              comment.user = JSON.parse(JSON.stringify(res.data))
              count += 1
              if (count === data.length) {
                this.commentsData = data
                this.$vs.loading.close()
              }
            })
          })
        })
      this.$store.dispatch('comment/countComments', { query: this.query })
    },
    paginationSetPageSize(items) {
      this.limit = items
      this.updateTable()
    },
    setColumnFilter(column, val) {
      if (val.length > 0) {
        const filter = []
        let i = null
        for (i in val) {
          filter.push(val[i].value ? val[i].value : val[i].label)
        }
        this.query[column] = { $in: filter }
      } else {
        delete this.query[column]
      }
      this.currentPage = 1
      this.updateTable()
    },
    resetColFilters() {
      this.typeFilter = []
      this.query = {}
      this.currentPage = 1
      this.updateTable()
      this.$refs.filterCard.removeRefreshAnimation()
    },
    handleSort(col, active) {
      if (col === null) return
      if (active !== null) {
        this.sort[col] = active === 'asc' ? 1 : -1
      } else {
        this.sort = {}
      }
      this.currentPage = 1
      this.updateTable()
    }
  },
  created() {
    if (!moduleComment.isRegistered) {
      this.$store.registerModule('comment', moduleComment)
      moduleComment.isRegistered = true
    }
    if (!moduleUserManagement.isRegistered) {
      this.$store.registerModule('userManagement', moduleUserManagement)
      moduleUserManagement.isRegistered = true
    }
    this.updateTable()
  }
}
</script>

<style lang="scss">
#page-comment-list {
}
</style>
